import { useEffect, useRef } from "react";
import styles from "./ParticleBackground.module.css";

const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let animationId;
    let snowflakes = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const FLAKE_COUNT = Math.min(
      Math.floor((canvas.width * canvas.height) / 10000),
      150
    );

    // Rainbow hues spread across the spectrum
    const rainbowColors = [
      { r: 255, g: 60, b: 60 },    // Red
      { r: 255, g: 120, b: 50 },   // Orange
      { r: 255, g: 220, b: 50 },   // Yellow
      { r: 80, g: 255, b: 80 },    // Green
      { r: 50, g: 200, b: 255 },   // Cyan
      { r: 80, g: 120, b: 255 },   // Blue
      { r: 160, g: 80, b: 255 },   // Indigo
      { r: 255, g: 80, b: 220 },   // Violet/Pink
    ];

    class Snowflake {
      constructor() {
        this.reset(true);
      }

      reset(initial = false) {
        this.x = Math.random() * canvas.width;
        this.y = initial
          ? Math.random() * canvas.height
          : -Math.random() * 40 - 10;
        this.size = Math.random() * 1.5 + 0.5;
        this.speedY = Math.random() * 0.4 + 0.15;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.wobbleAmp = Math.random() * 30 + 10;
        this.wobbleSpeed = Math.random() * 0.02 + 0.005;
        this.wobbleOffset = Math.random() * Math.PI * 2;
        this.opacity = Math.random() * 0.7 + 0.3;

        // Pick a rainbow color with slight random variation
        const color =
          rainbowColors[Math.floor(Math.random() * rainbowColors.length)];
        this.r = Math.min(255, color.r + Math.floor((Math.random() - 0.5) * 30));
        this.g = Math.min(255, color.g + Math.floor((Math.random() - 0.5) * 30));
        this.b = Math.min(255, color.b + Math.floor((Math.random() - 0.5) * 30));
      }

      update(time) {
        // Gentle sine wobble for realistic drift
        this.x +=
          this.speedX +
          Math.sin(time * this.wobbleSpeed + this.wobbleOffset) * 0.4;
        this.y += this.speedY;
        this.rotation += this.rotSpeed;

        // Fade out near bottom
        if (this.y > canvas.height - 60) {
          this.opacity *= 0.97;
        }

        // Reset when off screen
        if (this.y > canvas.height + 20 || this.opacity < 0.02) {
          this.reset();
        }
        // Wrap horizontally
        if (this.x < -20) this.x = canvas.width + 20;
        if (this.x > canvas.width + 20) this.x = -20;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.r}, ${this.g}, ${this.b}, ${this.opacity})`;
        ctx.fill();
      }
    }

    // Init snowflakes
    for (let i = 0; i < FLAKE_COUNT; i++) {
      snowflakes.push(new Snowflake());
    }

    let time = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time++;
      snowflakes.forEach((s) => {
        s.update(time);
        s.draw();
      });
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.canvas} />;
};

export default ParticleBackground;
